import { CellAction, useCellActionsExecutor } from 'context/CellActions';
import { useCallToActionBrandProps } from 'modules/BrandDefinition/hooks';
import { confirmLexicalStateInCTAComponent } from 'modules/draftjs2lexical';
import { createLexicalCTAEditorEmptyState } from 'modules/Lexical/editors';
import { useLexicalCallToActionEditor } from 'modules/Lexical/hooks/useLexicalCallToActionEditor';
import { useLexicalStateCache } from 'modules/Lexical/hooks/useLexicalStateCache';
import { useLexicalUndo } from 'modules/Lexical/hooks/useLexicalUndo';
import React, { useMemo } from 'react';
import AccessibleDiv from 'components/AccessibleDiv/AccessibleDiv';
import Toolbar from 'components/Toolbar/CallToAction';
import UndoPortal from 'components/UndoRedo/UndoPortal';
import * as Constants from 'const';
import { getActiveLayer } from 'hooks/useActiveLayer';
import { useProjectType } from 'hooks/useProjectType';
import { AssetAlignment, CallToActionStyles } from 'models';
import { allowLinkRedirect } from 'utils/allowLinkRedirect';
import * as styleUtils from 'utils/styles';
import { useHistoryGlobalState } from '../../../hooks/useHistoryGlobalState';
import LexicalButton from './components/LexicalButton';
import ResizableButton from './components/ResizableButton';
import { LexicalCTAEditorProvider } from './context/LexicalCTAEditor';import { useCallToAction } from './hooks';
import { useHistoryExtraState } from './hooks/useHistoryExtraState';
import { useLink } from './hooks/useLink';
import { useResizeObserverCTA } from './hooks/useResizeObserverCTA';
import { useSave } from './hooks/useSave';
import { useSizeReset } from './hooks/useSizeReset';
import { useStyles } from './hooks/useStyles';
import { CallToActionProps } from './models';
import css from './styles.module.scss';

export function getCallToActionDOMId(relationId: string): string {
  return `call-to-action-cell-${relationId}`;
}

const CallToAction: React.FunctionComponent<CallToActionProps> = (props) => {
  const {
    cellHeight,
    cellWidth,
    disableCellWidthEditing,
    document: originalDocument,
    editMode,
    isIncreasingWidthDisabled,
    layoutId,
    maxCellHeight,
    minCellHeight,
    relation,
    toggleColumnWidth,
    toggleRowHeight,
    toggleAutoFitContent,
    isAutoFitContent,
    images,
    isDraggingAsset,
  } = props;

  const projectType = useProjectType();
  const activeLayer = getActiveLayer();
  const relationId = relation.get('id');
  const brandProps = useCallToActionBrandProps(relationId);

  const editorWrap = React.useRef<HTMLDivElement>(null);

  const stylesHook = useStyles(
    relation,
    activeLayer,
    { colors: brandProps.colors, images },
    Constants.ProjectsConfig[projectType],
    cellWidth,
  );

  const { styles, css: stylesCSS, getMaxWidth } = stylesHook;

  const sizeResetHook = useSizeReset(props, stylesHook);
  const document = useMemo(
    () => originalDocument && confirmLexicalStateInCTAComponent(
      originalDocument,
      relation.getIn(['styles', activeLayer]).toJS() as CallToActionStyles,
      projectType,
      brandProps.fonts,
    ),
    // fonts are intentionally skipped from the deps
    [originalDocument], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const lexicalState = useMemo(
    () => {
      const state = document?.get('lexicalState');
      if (state && state !== 'null') {
        return state;
      }

      return createLexicalCTAEditorEmptyState(document?.get('name'));
    },
    [document],
  );
  const editorHook = useLexicalCallToActionEditor(sizeResetHook, editorWrap);
  const cachedLexicalState = useLexicalStateCache(editorHook.editorRef, editMode, lexicalState);

  const { link, toggleLink } = useLink(document);

  const {
    container,
    modifiedStylesSetters: stylesSetters,
    correctedHeight,
    correctedWidth,
    getResizeComponentHeight,
    getResizeComponentMinHeight,
    getResizeComponentWidth,
    finishResizing,
    onResize,
  } = useCallToAction(props, stylesHook, sizeResetHook, editorWrap);

  useSave(props, stylesHook, sizeResetHook, editorHook, link);
  useResizeObserverCTA(container, editorWrap, styles, stylesSetters, editMode, isDraggingAsset);

  const {
    setCursorOnAbbreviation,
    applySelection,
    returnFocusToEditor,
  } = editorHook;

  const history = useHistoryExtraState(
    props,
    stylesHook,
    { link, toggleLink, height: correctedHeight, width: correctedWidth },
  );

  const undoHook = useLexicalUndo(editorHook.editorRef);
  useHistoryGlobalState(editMode, undoHook.isUndoDisabled);

  const handleClick = (event: React.MouseEvent): void => {
    if (allowLinkRedirect(event) && link) {
      window.open(link, '_blank');
    }
  };

  useCellActionsExecutor(CellAction.SET_CURSOR_ON_ABBREVIATION, setCursorOnAbbreviation);
  useCellActionsExecutor(CellAction.APPLY_SELECTION, applySelection);

  const buttonStyles = {
    ...stylesCSS.element,
    ...styleUtils.getButtonSize(correctedHeight, correctedWidth, editMode),
  };

  const LexicalButtonProps = {
    id: getCallToActionDOMId(relationId),
    editorState: cachedLexicalState,
    editorRef: editorHook.editorRef,
    onChange: editorHook.onChange,
    onStopEditing: editorHook.onStopEditing,
    cssButton: css.button,
    styles: buttonStyles,
    editMode,
    editorWrap,
    returnFocusToEditor,
    brandProps,
    history,
    historyOnChange: undoHook.onChange,
  };

  return (
    <AccessibleDiv
      className={css.CallToAction}
      onClick={handleClick}
      ref={container}
      style={stylesCSS.container}
    >
      <LexicalCTAEditorProvider value={LexicalButtonProps}>
        <div className={css.innerContainer}>
          {
            editMode
              ? <ResizableButton
                alignment={styles.alignment.toJS() as AssetAlignment}
                containerRef={container}
                finishResizing={finishResizing}
                onResize={onResize}
                width={getResizeComponentWidth()}
                height={getResizeComponentHeight()}
                minHeight={getResizeComponentMinHeight()}
              >
                <LexicalButton />
              </ResizableButton>
              : <LexicalButton />
          }
        </div>
      </LexicalCTAEditorProvider>
      {
        editMode &&
        <>
          <UndoPortal
            undo={undoHook.undo}
            redo={undoHook.redo}
            isRedoDisabled={undoHook.isRedoDisabled}
            isUndoDisabled={undoHook.isUndoDisabled}
          />
          <Toolbar
            editorProps={editorHook.props}
            editorSetters={editorHook.setters}
            styles={styles}
            stylesSetters={stylesSetters}
            projectType={projectType}
            returnFocusToEditor={returnFocusToEditor}
            cellHeight={cellHeight}
            maxCellHeight={maxCellHeight}
            minCellHeight={minCellHeight}
            toggleCellHeight={toggleRowHeight}
            cellWidth={cellWidth}
            toggleColumnWidth={toggleColumnWidth}
            link={link}
            toggleLink={toggleLink}
            layoutId={layoutId}
            disableCellWidthEditing={disableCellWidthEditing}
            height={getResizeComponentHeight()}
            width={getResizeComponentWidth()}
            maxWidth={getMaxWidth()}
            isIncreasingWidthDisabled={isIncreasingWidthDisabled}
            toggleAutoFitContent={toggleAutoFitContent}
            isAutoFitContent={isAutoFitContent}
          />
        </>
      }
    </AccessibleDiv>
  );
};

export default CallToAction;
